import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useHistory, useLocation } from 'react-router-dom'
import { useUserList } from 'app/hooks/trade/userList'
import { IconUsers } from '@tabler/icons-react'

const NavigationContainer = styled.div`
  background: #252538;
  border-bottom: 1px solid rgba(92, 124, 250, 0.15);
  padding: 8px 50px;
  
  @media (max-width: 768px) {
    padding: 8px 5px;
  }
`

const TabsWrapper = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: visible;
  flex-wrap: wrap;
`

const TabItem = styled.button<{ $active?: boolean }>`
  position: relative;
  padding: 6px 12px;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, rgba(92, 124, 250, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
    : 'transparent'};
  border: 1px solid ${props => props.$active 
    ? 'rgba(92, 124, 250, 0.4)' 
    : 'transparent'};
  border-radius: 6px;
  color: ${props => props.$active ? '#5c7cfa' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 12px;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, rgba(92, 124, 250, 0.25) 0%, rgba(118, 75, 162, 0.25) 100%)'
      : 'rgba(255, 255, 255, 0.05)'};
    color: ${props => props.$active ? '#5c7cfa' : '#fff'};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  ${props => props.$active && `
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 40%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #5c7cfa, transparent);
    }
  `}
`

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 10px;
  font-weight: 600;
  border-radius: 8px;
  margin-left: 2px;
`

interface TabItem {
  title: string
  link: string
  comp?: React.ReactNode
  count?: number
  icon?: React.ReactNode
}

interface ModernTabNavigationProps {
  items: TabItem[]
}

const ModernTabNavigation: React.FC<ModernTabNavigationProps> = ({ items }) => {
  const location = useLocation()
  const navigation = useHistory()
  const { userlist, sendwho } = useUserList()
  const [activeTab, setActiveTab] = useState('')

  useEffect(() => {
    sendwho()
  }, [])

  useEffect(() => {
    const currentPath = location.pathname
    const activeItem = items.find(item => {
      if (currentPath === '/users' && item.link === '/users/0') return true
      return item.link === currentPath
    })
    if (activeItem) {
      setActiveTab(activeItem.link)
    }
  }, [location.pathname, items])

  const handleTabClick = (link: string) => {
    navigation.push(link)
    setActiveTab(link)
  }

  return (
    <NavigationContainer>
      <TabsWrapper>
        {items.map((item, index) => {
          const isActive = activeTab === item.link || 
            (location.pathname === '/users' && item.link === '/users/0' && index === 0)
          
          return (
            <TabItem
              key={item.link}
              $active={isActive}
              onClick={() => handleTabClick(item.link)}
            >
              {item.icon}
              {item.title}
              {item.title === '접속자수' && (
                <CountBadge>{userlist?.length || 0}</CountBadge>
              )}
              {item.count !== undefined && item.count > 0 && (
                <CountBadge>{item.count}</CountBadge>
              )}
            </TabItem>
          )
        })}
      </TabsWrapper>
    </NavigationContainer>
  )
}

export default React.memo(ModernTabNavigation)